export function extractApiErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === "object" && data !== null) {
    const d = data as Record<string, unknown>;
    if (typeof d.message === "object" && d.message !== null) {
      const inner = d.message as Record<string, unknown>;
      if (typeof inner.message === "string") return inner.message;
    }
    if (typeof d.message === "string") return d.message;
  }
  return "An unexpected error occurred.";
}

/**
 * Error codes the backend sends in `error_code` (its error body is
 * `{"status": "error", "error_code": "...", "message": "..."}` — there is no
 * `code` key). Facility lifecycle codes agreed with the backend for JAA-1117.
 */
export const API_ERROR_CODES = {
  /** 403 — non-admin read of a deactivated facility, or a blocked admin action on one. */
  FACILITY_DEACTIVATED: "FACILITY_DEACTIVATED",
  /** 403 on login, sent only after the password verifies. */
  USER_DEACTIVATED: "USER_DEACTIVATED",
  /** 409 — the lifecycle preview the admin confirmed is out of date; reload it. */
  PREVIEW_STALE: "PREVIEW_STALE",
  /** 409 — user add/update/remove/resend-invite in an org whose facilities are all deactivated. */
  ORGANIZATION_FULLY_DEACTIVATED: "ORGANIZATION_FULLY_DEACTIVATED",
  /** 409 — deactivate on a facility that's already deactivated. Treated as already done. */
  FACILITY_ALREADY_DEACTIVATED: "FACILITY_ALREADY_DEACTIVATED",
  /** 409 — reactivate on a facility that isn't deactivated. Treated as already done. */
  FACILITY_NOT_DEACTIVATED: "FACILITY_NOT_DEACTIVATED",
  /** 409 — deactivate refused because the facility isn't Active (e.g. still onboarding). Nothing changed. */
  FACILITY_NOT_ACTIVE: "FACILITY_NOT_ACTIVE",
  /** 409 — delete refused because the facility can't be deleted in its status (e.g. still onboarding). Nothing changed. */
  FACILITY_NOT_DELETABLE: "FACILITY_NOT_DELETABLE",
} as const;

/** The backend's `error_code` for a failed request, if it sent one. */
export function getApiErrorCode(err: unknown): string | undefined {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (typeof data !== "object" || data === null) return undefined;
  const code = (data as Record<string, unknown>).error_code;
  return typeof code === "string" ? code : undefined;
}

/**
 * True when sign-in was refused because the account is deactivated
 * (JAA-1117) — e.g. a facility change or an admin turned it off. The login
 * endpoint answers 403 with `error_code: "USER_DEACTIVATED"`, only after the
 * password verifies. Matched on the code, not the message text, so backend
 * copy changes can't break it.
 */
export function isAccountDeactivatedError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 403 && getApiErrorCode(err) === API_ERROR_CODES.USER_DEACTIVATED;
}

/**
 * True when `err` is the 403 the backend returns for a facility-scoped
 * request once that facility has been deactivated. Lets pages show "this
 * facility has been deactivated" instead of a generic error — the case a
 * stale bookmark or typed URL to an old facility ID hits.
 *
 * Matches `error_code: "FACILITY_DEACTIVATED"` (JAA-1117). Also still
 * matches the older body `{"detail": "Facility is deactivated"}` (FastAPI's
 * default HTTPException shape) that the backend returns today, until every
 * facility route moves to the error_code shape.
 */
export function isFacilityDeactivatedError(err: unknown): boolean {
  const response = (err as { response?: { status?: number; data?: unknown } })
    ?.response;
  if (response?.status !== 403) return false;
  if (getApiErrorCode(err) === API_ERROR_CODES.FACILITY_DEACTIVATED) return true;
  const data = response.data;
  if (typeof data !== "object" || data === null) return false;
  const d = data as Record<string, unknown>;
  const text = [d.detail, d.message].find((v) => typeof v === "string") as
    | string
    | undefined;
  return text?.toLowerCase().includes("deactivated") ?? false;
}

/**
 * True when `err` means the facility doesn't exist or the user has no access
 * to it: a 404 (backend's "facility not found", which it also returns for a
 * facility outside the user's organization) or any 403 that isn't the
 * deactivated one above. Used by RequireFacility to show FacilityNotFoundPage
 * for a typed/stale facility URL instead of letting every page 404 into
 * "No data available".
 */
export function isFacilityInaccessibleError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 404) return true;
  return status === 403 && !isFacilityDeactivatedError(err);
}
